import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, computed, effect, signal, untracked } from '@angular/core';
import { BaseServiceResponse } from '../../models/api-calls/base-service-response';
import { SessionService } from '../account-service/session.service';
import { ApiWrapperService } from '../api-wrapper-service/api-wrapper-services';

export type BrowserNotificationPermission = NotificationPermission | 'unsupported';

@Injectable({ providedIn: 'root' })
export class BrowserPushService {
  // Private variables

  private identity = '';
  private sessionVersion = 0;
  private readonly configurationKey = signal('');
  private pendingOperation: Promise<unknown> = Promise.resolve();
  private deviceCleanup: Promise<void> = Promise.resolve();

  // Public properties

  public readonly supported = signal(false);
  public readonly permission = signal<BrowserNotificationPermission>('unsupported');
  public readonly subscribed = signal(false);
  public readonly working = signal(false);
  public readonly testing = signal(false);
  public readonly loading = signal(false);
  public readonly error = signal('');
  public readonly message = signal('');
  public readonly busy = computed(() => this.working() || this.testing() || this.loading());
  public readonly configured = computed(() => !!this.configurationKey() && this.supported());

  // Constructors

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly api: ApiWrapperService,
    private readonly session: SessionService
  ) {
    this.updateCapabilities();

    effect(() => {
      const account = this.session.account();
      const identity = this.session.status() === 'authenticated' && account?.id
        ? `${account.id}:${this.session.version}` : '';

      untracked(() => this.switchIdentity(identity));
    });
  }

  // Public methods

  public async refresh(): Promise<void> {
    if (!this.identity || this.busy()) return;

    const version = this.sessionVersion;
    const operation = this.loadCurrentDevice(version);
    this.pendingOperation = operation;
    await operation;
  }

  public async enable(): Promise<void> {
    if (!this.identity || this.busy()) return;

    this.error.set('');
    this.message.set('');
    this.updateCapabilities();

    if (!this.supported()) {
      this.error.set('Notifications aren’t available in this browser. Try a compatible browser with a secure (https) connection.');
      return;
    }

    if (!this.configurationKey()) {
      this.error.set('Push notifications are unavailable right now. Refresh device status to check again.');
      return;
    }

    if (this.permission() === 'denied') {
      this.error.set('Notifications are blocked. Allow them for this site in your browser settings first.');
      return;
    }

    const version = this.sessionVersion;
    this.working.set(true);

    // Permission must be requested directly from a user gesture, before awaiting network or worker activity.
    const operation = this.enableSubscription(version);
    this.pendingOperation = operation;
    await operation;
  }

  public async disable(): Promise<void> {
    if (!this.identity || this.busy() || !this.supported()) return;

    const version = this.sessionVersion;
    this.working.set(true);
    this.error.set('');
    this.message.set('');

    const operation = this.disableSubscription(version);
    this.pendingOperation = operation;
    await operation;
  }

  public async sendTest(): Promise<void> {
    if (!this.identity || this.busy() || !this.supported() || !this.subscribed()) return;

    const version = this.sessionVersion;
    this.testing.set(true);
    this.error.set('');
    this.message.set('');

    const operation = this.sendTestNotification(version);
    this.pendingOperation = operation;
    await operation;
  }

  // Private methods

  private switchIdentity(identity: string): void {
    if (identity === this.identity) return;

    const wasAuthenticated = !!this.identity;
    const oldOperation = this.pendingOperation;
    this.identity = identity;
    this.sessionVersion++;
    this.configurationKey.set('');
    this.loading.set(false);
    this.working.set(false);
    this.testing.set(false);
    this.subscribed.set(false);
    this.error.set('');
    this.message.set('');

    if (wasAuthenticated) {
      // A browser endpoint must never continue receiving another account's pushes after sign-out.
      // Wait for an in-flight registration before removing the browser endpoint locally.
      this.deviceCleanup = this.deviceCleanup.then(async () => {
        await oldOperation.catch(() => undefined);
        await this.unsubscribeLocally();
      });
    }

    if (identity) void this.refresh();
  }

  private updateCapabilities(): void {
    const browser = this.document.defaultView;
    const available = !!browser && browser.isSecureContext && 'serviceWorker' in browser.navigator &&
      'PushManager' in browser && 'Notification' in browser;

    this.supported.set(available);
    this.permission.set(typeof Notification === 'undefined' ? 'unsupported' : Notification.permission);
  }

  private async loadCurrentDevice(version: number): Promise<void> {
    this.loading.set(true);
    this.error.set('');
    this.updateCapabilities();

    try {
      if (!this.supported()) return;

      await this.deviceCleanup;
      if (!this.isCurrent(version)) return;

      const configuration = BaseServiceResponse.fromServiceResponse(await this.api.getWebPushConfiguration());
      if (!this.isCurrent(version)) return;

      if (!configuration.succeeded || !configuration.payload?.vapidPublicKey) {
        this.error.set('Push notifications are unavailable right now. Refresh device status to check again.');
        return;
      }

      this.configurationKey.set(configuration.payload.vapidPublicKey);

      const registration = await this.registerWorker();
      const subscription = await registration.pushManager.getSubscription();
      if (!this.isCurrent(version)) return;

      if (subscription) {
        const registered = await this.registerWithServer(subscription, version);
        if (this.isCurrent(version)) this.subscribed.set(registered);
      } else {
        this.subscribed.set(false);
      }
    } catch {
      if (this.isCurrent(version)) this.error.set('We couldn’t check notifications on this device. Check your connection, then refresh device status.');
    } finally {
      if (this.isCurrent(version)) this.loading.set(false);
    }
  }

  private async enableSubscription(version: number): Promise<void> {
    try {
      const permission = this.permission() === 'granted' ? 'granted' : await Notification.requestPermission();
      if (!this.isCurrent(version)) return;

      this.permission.set(permission);
      if (permission !== 'granted') {
        this.error.set(permission === 'denied'
          ? 'Notifications are blocked for Square Control. Allow them in your browser settings.'
          : 'Notifications weren’t allowed. You can turn them on later in your browser settings.');
        return;
      }

      await this.deviceCleanup;
      if (!this.isCurrent(version)) return;

      const registration = await this.registerWorker();
      const existing = await registration.pushManager.getSubscription();
      const subscription = existing ?? await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.decodeVapidKey(this.configurationKey())
      });

      if (!this.isCurrent(version)) {
        if (!existing) await subscription.unsubscribe();
        return;
      }

      if (await this.registerWithServer(subscription, version) && this.isCurrent(version)) {
        this.subscribed.set(true);
        this.message.set('Push notifications are enabled for this browser.');
      }
    } catch {
      if (this.isCurrent(version)) {
        this.updateCapabilities();
        this.error.set(this.permission() === 'denied'
          ? 'Notifications are blocked. Change this site’s browser permission to enable them.'
          : 'Notifications could not be enabled on this device. Check your browser’s notification settings and try again.');
      }
    } finally {
      if (this.isCurrent(version)) this.working.set(false);
    }
  }

  private async disableSubscription(version: number): Promise<void> {
    try {
      await this.deviceCleanup;
      if (!this.isCurrent(version)) return;

      const subscription = await (await this.registerWorker()).pushManager.getSubscription();
      if (!this.isCurrent(version)) return;

      if (!subscription) {
        this.subscribed.set(false);
        return;
      }

      const response = BaseServiceResponse.fromServiceResponse(await this.api.unregisterWebPushSubscription({
        endpoint: subscription.endpoint
      }));
      if (!this.isCurrent(version)) return;

      if (!response.succeeded) {
        this.error.set('We couldn’t confirm whether notifications were disabled. Refresh device status before trying again.');
        return;
      }

      if (!(await subscription.unsubscribe())) {
        this.error.set('Square Control disabled notifications for this device, but the browser could not finish the change. Choose Disable on this device again.');
        return;
      }

      if (this.isCurrent(version)) {
        this.subscribed.set(false);
        this.message.set('Push notifications are disabled for this browser.');
      }
    } catch {
      if (this.isCurrent(version)) this.error.set('We couldn’t confirm whether notifications were disabled. Refresh device status before trying again.');
    } finally {
      if (this.isCurrent(version)) this.working.set(false);
    }
  }

  private async sendTestNotification(version: number): Promise<void> {
    try {
      const subscription = await (await this.registerWorker()).pushManager.getSubscription();
      if (!this.isCurrent(version)) return;

      if (!subscription) {
        this.subscribed.set(false);
        this.error.set('Notifications are no longer enabled in this browser. Choose Enable on this device to turn them on again.');
        return;
      }

      const response = BaseServiceResponse.fromServiceResponse(await this.api.sendTestWebPush({ endpoint: subscription.endpoint }));
      if (!this.isCurrent(version)) return;

      if (response.succeeded) {
        this.message.set('The test was sent for delivery. Check your device’s notifications and Do Not Disturb settings.');
      } else {
        this.error.set(response.messages[0] ?? 'We couldn’t send the test notification. Please try again.');
      }
    } catch {
      if (this.isCurrent(version)) this.error.set('We couldn’t confirm whether the test was sent. Check your connection.');
    } finally {
      if (this.isCurrent(version)) this.testing.set(false);
    }
  }

  private async registerWithServer(subscription: PushSubscription, version: number): Promise<boolean> {
    const keys = subscription.toJSON().keys;
    if (!keys?.['p256dh'] || !keys?.['auth']) {
      if (this.isCurrent(version)) this.error.set('This browser could not finish setting up notifications. Refresh device status and try again.');
      return false;
    }

    try {
      const response = BaseServiceResponse.fromServiceResponse(await this.api.registerWebPushSubscription({
        endpoint: subscription.endpoint,
        p256Dh: keys['p256dh'],
        auth: keys['auth'],
        expiresAt: subscription.expirationTime ? new Date(subscription.expirationTime).toISOString() : null
      }));

      if (!this.isCurrent(version)) return false;
      if (response.succeeded) return true;

      this.error.set(response.messages[0] ?? 'We couldn’t enable notifications on this device. Please try again.');
    } catch {
      if (this.isCurrent(version)) this.error.set('We couldn’t confirm whether notifications were enabled. Refresh device status to check.');
    }
    return false;
  }

  private async registerWorker(): Promise<ServiceWorkerRegistration> {
    const workers = this.document.defaultView!.navigator.serviceWorker;
    await workers.register('/push-worker.js', { scope: '/' });

    // Subscribe only after the worker becomes active; first-time installs can still be installing.
    return workers.ready;
  }

  private async unsubscribeLocally(): Promise<void> {
    try {
      const worker = this.document.defaultView?.navigator.serviceWorker;
      const registration = await worker?.getRegistration('/');
      await (await registration?.pushManager.getSubscription())?.unsubscribe();
    } catch {
      // Browser permissions may have changed. The endpoint will be removed by the provider on expiry.
    }
  }

  private decodeVapidKey(key: string): ArrayBuffer {
    const padded = key.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(key.length / 4) * 4, '=');
    const binary = this.document.defaultView!.atob(padded);
    return Uint8Array.from(binary, character => character.charCodeAt(0)).buffer;
  }

  private isCurrent(version: number): boolean {
    return !!this.identity && version === this.sessionVersion && this.session.status() === 'authenticated' &&
      this.identity === `${this.session.account()?.id}:${this.session.version}`;
  }
}
